import React from 'react';
import { Button, Footer, SEO, AppStoreBadges, PLAY_STORE_URL, APP_STORE_URL } from '@coupling/ui';
import Header from '../components/Header';
import VideoEmbed from '../components/VideoEmbed';
import { useBooking } from '../components/BookingProvider';
import { seoConfig } from '../config/seo';
import { videos } from '../config/videos';
import {
  demoTopics,
  existingPosNote,
  ownPosAction,
  ownPosFeatureList,
  ownPosHeadline,
  primaryDemoCta,
} from '../content/siteMessaging';

const isVideoReady = !videos.howToMintTokens.youtubeId.startsWith('PLACEHOLDER');

const steps = [
  {
    number: 1,
    title: 'Take Cards on Your Phone',
    description:
      'Tap to Pay on the phone you already carry, or an optional Stripe reader available through Coupling.',
  },
  {
    number: 2,
    title: ownPosHeadline,
    description: `One system handles ${ownPosFeatureList}. ${existingPosNote}`,
  },
  {
    number: 3,
    title: 'Catalog, Checkout, Mixed Tender',
    description:
      'Put items in a catalog, take the order, and split the ticket across cash, card, and rewards. Time the order when the shop needs it.',
  },
  {
    number: 4,
    title: 'Let Customers Book Appointments',
    description:
      'Massage, chiropractic, and other appointment businesses can take bookings in the same system that handles checkout.',
  },
  {
    number: 5,
    title: 'Add Rewards and Local Partners When Ready',
    description:
      'Issue rewards, vouchers, coupons, or gift cards from the same sale. Later, connect with other WNC merchants and cap how much of their value you accept.',
  },
];

const footerColumns = [
  {
    title: 'Product',
    links: [
      { label: 'What you can run tomorrow', href: '/#product-modes' },
      { label: 'POS workflow', href: '/#pos-workflow' },
      { label: 'Pricing', href: '/#pricing' },
    ],
  },
  {
    title: 'Mobile App',
    links: [
      { label: 'Get the App', href: '/app' },
      { label: 'Google Play', href: PLAY_STORE_URL },
      { label: 'App Store', href: APP_STORE_URL },
    ],
  },
  {
    title: 'Network',
    links: [
      { label: 'Coupling (Global)', href: 'https://cplng.org' },
      { label: 'Blog', href: '/blog' },
    ],
  },
  {
    title: 'Legal',
    links: [{ label: 'Privacy Policy', href: '/privacy' }],
  },
];

export const HowItWorks: React.FC = () => {
  const booking = useBooking();

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`How It Works - ${seoConfig.siteName}`}
        description={`See how Coupling WNC works for Western North Carolina merchants: Tap to Pay on your phone, ${ownPosAction}, mix cash, card, and rewards, book appointments, and add local rewards when you are ready.`}
        canonical={`${seoConfig.siteUrl}/how-it-works`}
        ogImage={seoConfig.defaultOgImage}
        ogUrl={`${seoConfig.siteUrl}/how-it-works`}
      />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-b from-brand-primary/5 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
            How Coupling WNC Works
          </h1>
          <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
            Follow the real operating sequence: take cards on your phone, {ownPosAction},
            mix tenders, book appointments, then add rewards
            and local partners when it makes sense.
          </p>
        </div>
      </section>

      {/* Video Section — only shown when the real video is available */}
      {isVideoReady && (
        <section className="pb-20 px-4">
          <div className="max-w-4xl mx-auto">
            <VideoEmbed
              youtubeId={videos.howToMintTokens.youtubeId}
              title={videos.howToMintTokens.title}
              className="shadow-2xl"
            />
          </div>
        </section>
      )}

      {/* Step-by-Step Walkthrough */}
      <section className="py-20 px-4 bg-bgSubtle">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-neutral-900 mb-12 text-center">
            Step-by-Step Guide
          </h2>
          <div className="space-y-8">
            {steps.map((step) => (
              <div
                key={step.number}
                className="flex gap-6 bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-neutral-100"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-brand-primary text-white flex items-center justify-center text-xl font-bold">
                  {step.number}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-neutral-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-neutral-900 mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-neutral-600 mb-8">
            See {demoTopics}
            with the way your WNC shop already sells.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="primary"
              size="lg"
              onClick={() => booking.open('demo')}
            >
              {primaryDemoCta}
            </Button>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => (window.location.href = '/guide')}
            >
              Read the Full Guide
            </Button>
          </div>
          <div className="mt-10">
            <p className="text-sm text-neutral-500 mb-3">
              Also available on mobile
            </p>
            <div className="flex justify-center">
              <AppStoreBadges
                playStoreUrl={PLAY_STORE_URL}
                appStoreUrl={APP_STORE_URL}
                size="md"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer
        description="Helping WNC merchants take payments, book appointments, and keep more value in mountain communities."
        columns={footerColumns}
        copyright={`\u00A9 ${new Date().getFullYear()} Coupling WNC. Part of the Coupling network.`}
      />
    </div>
  );
};

export default HowItWorks;
